import {
  themeSwitch,
  type ThemeSwitchMsg,
  type ThemeSwitchState,
} from '@llui/components/theme-switch';
import { button, component, island, path, span, svg, text, type Mountable } from '@llui/dom';

import { getTheme, setTheme, type ThemeMode } from '../theme.js';

/**
 * `<ThemeToggle>` — DungeonLogs' icon treatment over LLui's standard
 * light/dark/system theme switch.
 *
 * LLui owns the tri-state state machine and button contract. This adapter keeps
 * only product-specific concerns: the icon, translated current-mode label, and
 * persistence through the shared theme controller.
 */

export interface ThemeToggleLabels {
  light: string;
  dark: string;
  system: string;
}

export interface ThemeToggleOptions {
  labels: ThemeToggleLabels;
  /** Called after the theme cycles. The new mode is passed. */
  onChange?: (next: ThemeMode) => void;
  /** Forwarded `data-testid`. */
  testid?: string;
}

const SUN_PATH =
  'M12 3v2m0 14v2M5.05 5.05l1.41 1.41m11.08 11.08l1.41 1.41M3 12h2m14 0h2M5.05 18.95l1.41-1.41m11.08-11.08l1.41-1.41M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z';
const MOON_PATH = 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z';
const MONITOR_PATH =
  'M3 4h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm5 17h8m-4-4v4';

function pathFor(mode: ThemeMode): string {
  return mode === 'light' ? SUN_PATH : mode === 'dark' ? MOON_PATH : MONITOR_PATH;
}

function labelFor(mode: ThemeMode, labels: ThemeToggleLabels): string {
  return mode === 'light' ? labels.light : mode === 'dark' ? labels.dark : labels.system;
}

interface ThemeToggleEffect {
  type: 'apply-theme';
  mode: ThemeMode;
}

export function themeToggle(opts: ThemeToggleOptions): Mountable {
  const def = component<ThemeSwitchState, ThemeSwitchMsg, ThemeToggleEffect>({
    name: 'ThemeToggle',
    init: () => [themeSwitch.init(), []],
    update: (state, msg) => {
      const [next] = themeSwitch.update(state, msg);
      const effects: ThemeToggleEffect[] =
        next.theme === state.theme ? [] : [{ type: 'apply-theme', mode: next.theme }];
      return [next, effects];
    },
    view: ({ state, send }) => {
      const parts = themeSwitch.connect(state, send, { id: 'dl-theme-toggle' });
      const mode = state.at('theme');
      const label = mode.map((value) => labelFor(value, opts.labels));

      return [
        button(
          {
            ...parts.toggle,
            class: 'btn btn-ghost btn-sm dl-theme-toggle',
            'aria-label': label,
            'data-mode': mode,
            'data-testid': opts.testid,
          },
          [
            span({ 'aria-hidden': 'true' }, [
              svg(
                {
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor',
                  'stroke-width': '1.5',
                  'stroke-linecap': 'round',
                  'stroke-linejoin': 'round',
                  width: '16',
                  height: '16',
                  'aria-hidden': 'true',
                  focusable: 'false',
                },
                [path({ d: mode.map(pathFor) })],
              ),
            ]),
            span({ class: 'sr-only' }, [text(label)]),
          ],
        ),
      ];
    },
    onEffect: (effect) => {
      setTheme(effect.mode);
      opts.onChange?.(effect.mode);
    },
  });

  return island({
    def,
    initialState: themeSwitch.init(getTheme()),
    reason: 'The standard theme switch owns private control state; DungeonLogs persists changes.',
  });
}
